import React, { useEffect, useRef, useState } from 'react';
import { HashRouter, Routes, Route, NavLink, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Alert, Button, CircularProgress } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import MyLocation from '@mui/icons-material/MyLocation';
import MapOutlined from '@mui/icons-material/MapOutlined';
import StorefrontPage from '../components/storefront/StorefrontPage';
import ClientPortal from '../components/client/ClientPortal';
import LegalDocsPage from '../components/LegalDocsPage';
import PaymentReturnPage from '../components/PaymentReturnPage';
import api from '../components/api/api';
import { publicTheme } from '../design/theme';
import { coordinates, nearbyPoints, mapSearchUrl } from './points';
import './client-app.css';
import PushStatus from './PushStatus';
import { readMobileSberAttempt, restoreMobileSberCart } from '../components/client/mobileSberLogin';
import { readTidAttempt, restoreTidCart } from '../components/client/TidLogin';

// WebView storage is app-private and excluded from Android backup. Keep only the
// client session, never employee credentials. Server validates it on each entry.
export function restoreClientSession() {
  const saved = localStorage.getItem('mobileClientSession');
  if (saved && !sessionStorage.getItem('clientPortalToken')) sessionStorage.setItem('clientPortalToken', saved);
}
if (process.env.REACT_APP_CLIENT_APP === 'true') {
  restoreClientSession();
  restoreMobileSberCart();
  restoreTidCart();
}

export function PointsScreen() {
  const navigate = useNavigate();
  const [points, setPoints] = useState([]), [loading, setLoading] = useState(true);
  const [error, setError] = useState(''), [geoMessage, setGeoMessage] = useState('');
  const [location, setLocation] = useState(null), [locating, setLocating] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true; setLoading(true); setError('');
    api.get('/showcase/public/points', { skipLoading: true }).then(({ data }) => {
      if (!Array.isArray(data)) throw new Error('Invalid points');
      if (active) setPoints(data.filter(Boolean));
    }).catch(() => { if (active) setError('Не удалось загрузить пункты. Проверьте подключение.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);
  const locate = () => {
    if (locating) return;
    if (!navigator.geolocation) { setGeoMessage('Геолокация недоступна. Выберите пункт из списка.'); return; }
    setLocating(true); setGeoMessage('');
    navigator.geolocation.getCurrentPosition(position => {
      setLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      setLocating(false);
      setGeoMessage('Местоположение определено. Для пунктов без координат расстояние пока недоступно.');
    }, () => { setLocating(false); setGeoMessage('Не удалось определить местоположение. Можно выбрать пункт вручную.'); },
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 });
  };
  return <section className="ma-points">
    <div className="ma-eyebrow">РЯДОМ С ВАМИ</div><h1>Пункты проката</h1>
    <p>Выберите удобный адрес и посмотрите доступные товары.</p>
    <div className="ma-map-placeholder" aria-label="Место для карты пунктов">
      <div className="ma-map-grid" aria-hidden="true"><MapOutlined /></div>
      <div className="ma-map-caption"><strong>Здесь будет карта пунктов</strong><span>Пока адрес можно открыть в картах ниже</span></div>
    </div>
    <Button fullWidth variant="outlined" startIcon={locating ? <CircularProgress size={18} /> : <MyLocation />} onClick={locate} disabled={locating}>
      {locating ? 'Определяем местоположение…' : 'Найти рядом со мной'}
    </Button>
    <p className="ma-note">Доступ к геолокации — по вашему разрешению. Координаты не сохраняем и не отправляем на сервер.</p>
    {geoMessage && <Alert severity="info">{geoMessage}</Alert>}
    {loading && <div className="ma-loading"><CircularProgress aria-label="Загрузка пунктов" /></div>}
    {error && <Alert severity="error" action={<Button onClick={() => setAttempt(value => value + 1)}>Повторить</Button>}>{error}</Alert>}
    {!loading && !error && !points.length && <Alert severity="info">Пока нет доступных пунктов с товарами.</Alert>}
    {nearbyPoints(points, location).map(point => <article className="ma-point" key={point.id}>
      <div className="ma-point-title"><LocationOnOutlined /><h2>{point.name}</h2>{point.distance != null && <span>{point.distance.toFixed(1)} км</span>}</div>
      <p>{point.address || 'Адрес уточняется'}</p>
      <p className="ma-note">{point.working_hours || 'Время работы уточняется'}</p>
      {!coordinates(point) && <span className="ma-note">Координаты пункта ещё не добавлены</span>}
      <div className="ma-point-actions"><Button variant="contained" onClick={() => navigate(`/?point=${point.id}`)}>Товары здесь</Button>
        <Button href={mapSearchUrl(point)} target="_blank" rel="noopener noreferrer">Открыть карту</Button></div>
    </article>)}
    <Link className="ma-document-link" to="/docs/privacy">Документы и контакты</Link>
  </section>;
}

function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const restorePendingSber = useRef(true);
  useEffect(() => {
    if (!restorePendingSber.current) return;
    restorePendingSber.current = false;
    if (readMobileSberAttempt() || readTidAttempt()) navigate('/my', { replace: true });
  }, [navigate]);
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    document.body.classList.add('client-app');
    const updateNetwork = () => setOffline(!navigator.onLine);
    const viewport = window.visualViewport;
    let baseline = window.innerHeight, scrollTimer;
    const resize = () => {
      const editing = /^(INPUT|TEXTAREA)$/.test(document.activeElement?.tagName || '');
      if (!editing) baseline = window.innerHeight;
      const keyboard = editing && (baseline - (viewport?.height || window.innerHeight) > 120);
      document.body.classList.toggle('ma-keyboard', keyboard);
      window.clearTimeout(scrollTimer);
      if (editing) scrollTimer = window.setTimeout(() => document.activeElement?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }), 180);
    };
    window.addEventListener('online', updateNetwork); window.addEventListener('offline', updateNetwork);
    window.addEventListener('resize', resize); viewport?.addEventListener('resize', resize);
    document.addEventListener('focusin', resize); document.addEventListener('focusout', resize);
    return () => {
      window.clearTimeout(scrollTimer);
      document.body.classList.remove('client-app', 'ma-keyboard');
      window.removeEventListener('online', updateNetwork); window.removeEventListener('offline', updateNetwork);
      window.removeEventListener('resize', resize); viewport?.removeEventListener('resize', resize);
      document.removeEventListener('focusin', resize); document.removeEventListener('focusout', resize);
    };
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return <div className="ma-app">
    <header className="ma-header"><span className="ma-brand"><StorefrontOutlined />ПрокатПрост<span>.</span></span><span>Аренда рядом</span></header>
    {offline && <Alert severity="warning">Нет подключения к интернету. Заказы и оплата доступны после восстановления связи.</Alert>}
    <PushStatus />
    <main className="ma-content"><Routes>
      <Route path="/" element={<StorefrontPage mobile />} />
      <Route path="/shop" element={<Navigate to="/" replace />} />
      <Route path="/points" element={<PointsScreen />} />
      <Route path="/my" element={<ClientPortal />} />
      <Route path="/payment/success" element={<PaymentReturnPage mobile />} />
      <Route path="/payment/failure" element={<PaymentReturnPage mobile failed />} />
      <Route path="/my/:token" element={<ClientPortal />} />
      <Route path="/me" element={<Navigate to="/my" replace />} />
      {['terms','offer','consent','refunds','cookies','deletion','contacts','privacy'].map(doc => <Route key={doc} path={`/docs/${doc}`} element={<LegalDocsPage doc={doc} />} />)}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></main>
    <nav className="ma-bottom-nav" aria-label="Навигация приложения">
      <NavLink to="/" end><StorefrontOutlined /><span>Каталог</span></NavLink>
      <NavLink to="/points"><LocationOnOutlined /><span>Пункты</span></NavLink>
      <NavLink to="/my"><PersonOutline /><span>Мой кабинет</span></NavLink>
    </nav>
  </div>;
}

export default function ClientApp() {
  return <ThemeProvider theme={publicTheme}><CssBaseline /><SnackbarProvider maxSnack={2}><HashRouter><MobileLayout /></HashRouter></SnackbarProvider></ThemeProvider>;
}
